import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getMyProfile, updateMyProfile,
  experienceApi, educationApi, skillsApi, languagesApi, certificationsApi,
  uploadResume, deleteResume,
} from '../../services/profileService';
import { COMPLETENESS_WEIGHTS } from './constants';

const EMPTY = {
  profile: {},
  experience: [],
  education: [],
  skills: [],
  languages: [],
  certifications: [],
  completeness: null,
};

const SECTION_APIS = {
  experience: experienceApi,
  education: educationApi,
  skills: skillsApi,
  languages: languagesApi,
  certifications: certificationsApi,
};

export function computeCompleteness(data) {
  const p = data.profile || {};
  const isBlueCollar = p.work_category === 'blue_collar';
  const filled = {
    basic: !!(p.first_name && p.phone),
    experience: (data.experience || []).length > 0,
    education: (data.education || []).length > 0,
    skills: (data.skills || []).length > 0,
    languages: (data.languages || []).length > 0,
    preferences: !!(p.expected_salary || (p.job_types || []).length || (p.preferred_locations || []).length),
    resume: !!p.resume_url,
  };
  let total = 0;
  let score = 0;
  Object.entries(COMPLETENESS_WEIGHTS).forEach(([key, weight]) => {
    if (key === 'resume' && isBlueCollar) return; // resume not counted for skilled work
    total += weight;
    if (filled[key]) score += weight;
  });
  return { percent: total ? Math.round((score / total) * 100) : 0, filled };
}

export default function useProfile() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const applyResponse = useCallback((res) => {
    setData({
      profile: res.profile || {},
      experience: res.experience || [],
      education: res.education || [],
      skills: res.skills || [],
      languages: res.languages || [],
      certifications: res.certifications || [],
      completeness: res.completeness ?? null,
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyProfile();
      applyResponse(res);
      setIsNew(false);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setData(EMPTY);
        setIsNew(true);
      } else {
        setError('Could not load your profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [applyResponse]);

  useEffect(() => {
    load();
    return () => clearTimeout(toastTimer.current);
  }, [load]);

  const saveProfile = useCallback(async (fields) => {
    const previous = data;
    // optimistic merge
    setData(d => ({ ...d, profile: { ...d.profile, ...fields } }));
    try {
      const res = await updateMyProfile(fields);
      applyResponse(res);
      setIsNew(false);
      showToast('Saved');
      return true;
    } catch (err) {
      setData(previous);
      showToast('Could not save. Please try again.', 'error');
      return false;
    }
  }, [data, applyResponse, showToast]);

  const sectionActions = useCallback((name) => {
    const api = SECTION_APIS[name];
    return {
      add: async (item) => {
        try {
          const created = await api.add(item);
          setData(d => ({ ...d, [name]: [...d[name], created] }));
          showToast('Added');
          return true;
        } catch {
          showToast('Could not save. Please try again.', 'error');
          return false;
        }
      },
      update: async (id, item) => {
        try {
          const updated = await api.update(id, item);
          setData(d => ({ ...d, [name]: d[name].map(x => (x.id === id ? updated : x)) }));
          showToast('Saved');
          return true;
        } catch {
          showToast('Could not save. Please try again.', 'error');
          return false;
        }
      },
      remove: async (id) => {
        try {
          await api.remove(id);
          setData(d => ({ ...d, [name]: d[name].filter(x => x.id !== id) }));
          showToast('Removed');
          return true;
        } catch {
          showToast('Could not remove. Please try again.', 'error');
          return false;
        }
      },
    };
  }, [showToast]);

  const resume = {
    upload: async (file) => {
      try {
        const res = await uploadResume(file);
        setData(d => ({ ...d, profile: { ...d.profile, ...res } }));
        showToast('Resume uploaded');
        return true;
      } catch {
        showToast('Upload failed. Use PDF or Word files under 5 MB.', 'error');
        return false;
      }
    },
    remove: async () => {
      try {
        await deleteResume();
        setData(d => ({
          ...d,
          profile: { ...d.profile, resume_url: null, resume_filename: null, resume_updated_at: null },
        }));
        showToast('Resume removed');
        return true;
      } catch {
        showToast('Could not remove resume.', 'error');
        return false;
      }
    },
  };

  const local = computeCompleteness(data);
  const completeness = data.completeness ?? local.percent;

  return {
    ...data,
    completeness,
    filledSections: local.filled,
    loading,
    error,
    isNew,
    toast,
    reload: load,
    saveProfile,
    sections: {
      experience: sectionActions('experience'),
      education: sectionActions('education'),
      skills: sectionActions('skills'),
      languages: sectionActions('languages'),
      certifications: sectionActions('certifications'),
    },
    resume,
  };
}
